import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ArchitectureIcon from '@mui/icons-material/Architecture'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BookmarksIcon from '@mui/icons-material/Bookmarks'
import CategoryIcon from '@mui/icons-material/Category'
import ConstructionIcon from '@mui/icons-material/Construction'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DesignServicesIcon from '@mui/icons-material/DesignServices'
import EngineeringIcon from '@mui/icons-material/Engineering'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import GroupsIcon from '@mui/icons-material/Groups'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import PlaceIcon from '@mui/icons-material/Place'
import SecurityIcon from '@mui/icons-material/Security'
import type { ReactElement } from 'react'

/**
 * The side menu, and the single source of truth for what a role can be granted.
 *
 * <p>This list is used twice, deliberately. The shell reads it to decide which links a
 * signed-in user sees; the role editor reads it to offer those same links as the thing
 * you grant. Keeping one list means a screen cannot exist in the menu but be ungrantable,
 * or be grantable but lead nowhere.</p>
 */
export interface Screen {
  label: string
  to: string
  icon: ReactElement
  section: string
  /** What the menu link itself requires before it appears. */
  permission?: string
  /** Shown when the user holds any one of these, for screens that span modules. */
  anyPermission?: string[]
  /**
   * The permission modules this screen governs.
   *
   * <p>"View" grants the VIEW action across them; "Full" grants every action. A module
   * may appear on more than one screen — material receipts are reachable from the entry
   * screen and from a daily report — so the granted set is the union across screens
   * rather than anything toggled per screen.</p>
   */
  modules: string[]
  /** Why someone would be given this, in the role editor. */
  hint?: string
}

export const SCREENS: Screen[] = [
  {
    label: 'Dashboard',
    to: '/',
    icon: <DashboardIcon />,
    section: 'Monitor',
    modules: ['DASHBOARD_SITE', 'DASHBOARD_MGMT'],
    hint: 'Site and management summaries',
  },
  {
    label: 'Programme',
    to: '/programme',
    icon: <AccountTreeIcon />,
    permission: 'WORK_ITEM:VIEW',
    section: 'Monitor',
    modules: ['WORK_ITEM'],
    hint: 'Activity network, float and the critical path',
  },

  // The four things a site engineer records every day. Their menu links are gated on
  // CREATE, so View alone shows the data through reports without offering the entry
  // screen - which is the right split for a reviewer.
  {
    label: 'Work completion',
    to: '/entry/work',
    icon: <ConstructionIcon />,
    permission: 'WORK_PROGRESS:CREATE',
    section: 'Daily entry',
    modules: ['WORK_PROGRESS', 'SITE_PHOTO'],
    hint: 'Quantities completed, with photographs',
  },
  {
    label: 'Labour worked',
    to: '/entry/labour',
    icon: <GroupsIcon />,
    permission: 'LABOUR_ENTRY:CREATE',
    section: 'Daily entry',
    modules: ['LABOUR_ENTRY'],
    hint: 'Headcount and hours by trade',
  },
  {
    label: 'Material received',
    to: '/entry/material',
    icon: <LocalShippingIcon />,
    permission: 'MATERIAL_RECEIPT:CREATE',
    section: 'Daily entry',
    modules: ['MATERIAL_RECEIPT'],
    hint: 'Deliveries accepted at the gate',
  },
  {
    label: 'Drawing received',
    to: '/entry/drawings',
    icon: <DesignServicesIcon />,
    permission: 'DRAWING:UPLOAD_REVISION',
    section: 'Daily entry',
    modules: ['DRAWING', 'DRAWING_FOLLOWUP'],
    hint: 'Drawing register and revisions',
  },
  {
    label: 'Templates',
    to: '/templates',
    icon: <BookmarksIcon />,
    anyPermission: ['WORK_PROGRESS:VIEW', 'LABOUR_ENTRY:VIEW', 'MATERIAL_RECEIPT:VIEW'],
    section: 'Daily entry',
    modules: ['WORK_PROGRESS', 'LABOUR_ENTRY', 'MATERIAL_RECEIPT'],
    hint: 'Saved entry sets; follows the three entry screens',
  },

  {
    label: 'Daily reports',
    to: '/daily-reports',
    icon: <AssignmentIcon />,
    permission: 'DAILY_REPORT:VIEW',
    section: 'Execution',
    modules: [
      'DAILY_REPORT',
      'SITE_ISSUE',
      'ATTACHMENT',
      'PLAN_WORK',
      'PLAN_LABOUR',
      'PLAN_MATERIAL',
    ],
    hint: 'The day as a whole: review, approval, issues and next-day plans',
  },
  {
    label: 'Pending drawings',
    to: '/drawings/pending',
    icon: <ArchitectureIcon />,
    permission: 'DRAWING:VIEW',
    section: 'Execution',
    modules: ['DRAWING', 'DRAWING_FOLLOWUP'],
    hint: 'What is awaited, and chasing it',
  },
  {
    label: 'Projects',
    to: '/projects',
    icon: <ApartmentIcon />,
    permission: 'PROJECT:VIEW',
    section: 'Execution',
    modules: ['PROJECT'],
  },
  {
    label: 'Sites',
    to: '/sites',
    icon: <PlaceIcon />,
    permission: 'SITE:VIEW',
    section: 'Execution',
    modules: ['SITE'],
  },
  {
    label: 'Site BOQ',
    to: '/site-boq',
    icon: <FormatListNumberedIcon />,
    permission: 'WORK_ITEM:VIEW',
    section: 'Execution',
    modules: ['WORK_ITEM'],
    hint: 'The bill each quantity is measured against',
  },

  {
    label: 'Materials',
    to: '/materials',
    icon: <Inventory2Icon />,
    permission: 'MASTER_MATERIAL:VIEW',
    section: 'Masters',
    modules: ['MASTER_MATERIAL', 'STOCK'],
  },
  {
    label: 'Reference data',
    to: '/reference',
    icon: <CategoryIcon />,
    permission: 'MASTER_MATERIAL:VIEW',
    section: 'Masters',
    modules: ['MASTER_LABOUR', 'MASTER_WORKPKG', 'PARTY'],
    hint: 'Trades, work packages, units, and the contractor and supplier lists',
  },

  {
    label: 'Users',
    to: '/admin/users',
    icon: <ManageAccountsIcon />,
    permission: 'USER:VIEW',
    section: 'Administration',
    modules: ['USER'],
    hint: 'Grants the ability to create accounts and reset passwords',
  },
  {
    label: 'Roles and permissions',
    to: '/admin/roles',
    icon: <SecurityIcon />,
    permission: 'ROLE:VIEW',
    section: 'Administration',
    modules: ['ROLE'],
    hint: 'Full access here lets a holder widen their own access',
  },

  {
    label: 'My profile',
    to: '/profile',
    icon: <EngineeringIcon />,
    section: 'Account',
    modules: [],
  },
]

/** Menu sections in the order they appear, derived so the two can never drift. */
export const SCREEN_SECTIONS: string[] = [...new Set(SCREENS.map((s) => s.section))]

/** Every module any screen governs. Anything outside this is not grantable by screen. */
export const SCREEN_MODULES: Set<string> = new Set(SCREENS.flatMap((s) => s.modules))

export type AccessLevel = 'none' | 'view' | 'full'

/**
 * The permissions a screen grants at a level, given the catalogue.
 *
 * <p>View is the read actions; Full is everything the module offers. The screen's own
 * menu gate is included at Full so that granting full access always makes the link
 * appear — several entry screens are gated on CREATE rather than VIEW.</p>
 */
export function permissionsFor(
  screen: Screen,
  level: AccessLevel,
  byModule: Map<string, string[]>,
): string[] {
  if (level === 'none') {
    return []
  }
  const all = screen.modules.flatMap((m) => byModule.get(m) ?? [])
  if (level === 'full') {
    const gate = [screen.permission, ...(screen.anyPermission ?? [])].filter(Boolean) as string[]
    return [...new Set([...all, ...gate])]
  }
  return all.filter((code) => code.endsWith(':VIEW'))
}
